import React from "react";
import { SvgIcon } from "./SvgIcon";
import styles from "./ErrorBoundary.module.scss";

interface Props {
  children?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Top-level error boundary. Catches React render errors that would otherwise
 * leave the user with a blank page, and shows a friendly fallback with a
 * Reload action.
 *
 * Render errors only — for unhandled promise rejections and runtime errors
 * outside of React's render cycle, see the global handlers in index.tsx.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught a render error:", error, info);
  }

  private handleReload = () => window.location.reload();

  render() {
    if (!this.state.hasError) {
      return this.props.children ?? null;
    }

    return (
      <div className={styles.fallbackRoot}>
        <div className={styles.fallbackCard}>
          <div className={styles.iconWrap}>
            <SvgIcon name="errorWhite" attributes={{ width: "32px", height: "32px" }} />
          </div>
          <h1 className={styles.title}>Something went wrong</h1>
          <p className={styles.body}>
            The page encountered an unexpected problem. Reloading usually clears this up. If it
            keeps happening, please contact support.
          </p>
          <button
            type="button"
            className="btn btn-primary font-size-13 px-32"
            onClick={this.handleReload}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
